import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Mail, Users, FileText, Award, MessageSquare, Briefcase, Shield, UserPlus, ClipboardList, Home, Settings, LogOut, Menu, Lock, ScrollText, Building2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { GmedLogo } from '../components/Logo';
import HomeTab from '../components/admin/HomeTab';
import ProductsTab from '../components/admin/ProductsTab';
import ContactsTab from '../components/admin/ContactsTab';
import TeamTab from '../components/admin/TeamTab';
import BlogTab from '../components/admin/BlogTab';
import CertificationsTab from '../components/admin/CertificationsTab';
import TestimonialsTab from '../components/admin/TestimonialsTab';
import PortfolioTab from '../components/admin/PortfolioTab';
import ComplianceTab from '../components/admin/ComplianceTab';
import CustomPagesTab from '../components/admin/CustomPagesTab';
import DPOContactsTab from '../components/admin/DPOContactsTab';
import JobApplicationsTab from '../components/admin/JobApplicationsTab';
import SettingsTab from '../components/admin/SettingsTab';
import SecurityTab from '../components/admin/SecurityTab';
import MenuSettingsTab from '../components/admin/MenuSettingsTab';
import FooterSettingsTab from '../components/admin/FooterSettingsTab';
import { authAPI, setAuthToken } from '../services/api';
import { toast } from 'sonner';

export const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('gmed_admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await authAPI.me();
      setUser(response.data);
    } catch (error) {
      setAuthToken(null);
      // On 401 the response interceptor already flags the expired session and redirects:
      // navigating here too would remount the login page and swallow the notice.
      if (error.response?.status !== 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    toast.success('Logout effettuato');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Header */}
      <div className="border-b border-border bg-gradient-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/" className="p-2 rounded-lg glass hover:bg-white/10 transition-colors" data-testid="back-to-home">
                <ArrowLeft className="w-5 h-5 text-white" />
              </Link>
              <div>
                <h1 className="font-display font-bold text-2xl sm:text-3xl text-white">Admin Dashboard</h1>
                <p className="text-gray-300 text-sm">Benvenuto, {user?.name || 'Admin'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <GmedLogo className="h-10 sm:h-12 w-auto" />
              <Button 
                variant="ghost" 
                className="text-white hover:text-white hover:bg-white/10"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="home" className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 h-auto p-2 bg-muted/50 rounded-lg">
            <TabsTrigger value="home" className="flex items-center space-x-2" data-testid="tab-home">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center space-x-2" data-testid="tab-products">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Prodotti</span>
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center space-x-2" data-testid="tab-contacts">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Contatti</span>
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center space-x-2" data-testid="tab-team">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Team</span>
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center space-x-2" data-testid="tab-blog">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">News</span>
            </TabsTrigger>
            <TabsTrigger value="certifications" className="flex items-center space-x-2" data-testid="tab-certifications">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Certificazioni</span>
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="flex items-center space-x-2" data-testid="tab-testimonials">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Testimonianze</span>
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="flex items-center space-x-2" data-testid="tab-portfolio">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Portfolio</span>
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center space-x-2" data-testid="tab-compliance">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Compliance</span>
            </TabsTrigger>
            <TabsTrigger value="pages" className="flex items-center space-x-2" data-testid="tab-pages">
              <ScrollText className="w-4 h-4" />
              <span className="hidden sm:inline">Pagine</span>
            </TabsTrigger>
            <TabsTrigger value="dpo" className="flex items-center space-x-2" data-testid="tab-dpo-contacts">
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">DPO</span>
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center space-x-2" data-testid="tab-jobs">
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">Candidature</span>
            </TabsTrigger>
            <TabsTrigger value="menu" className="flex items-center space-x-2" data-testid="tab-menu">
              <Menu className="w-4 h-4" />
              <span className="hidden sm:inline">Menu</span>
            </TabsTrigger>
            <TabsTrigger value="footer" className="flex items-center space-x-2" data-testid="tab-footer">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Footer</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center space-x-2" data-testid="tab-settings">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center space-x-2" data-testid="tab-security">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Sicurezza</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home"><HomeTab /></TabsContent>
          <TabsContent value="products"><ProductsTab /></TabsContent>
          <TabsContent value="contacts"><ContactsTab /></TabsContent>
          <TabsContent value="team"><TeamTab /></TabsContent>
          <TabsContent value="blog"><BlogTab /></TabsContent>
          <TabsContent value="certifications"><CertificationsTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="portfolio"><PortfolioTab /></TabsContent>
          <TabsContent value="compliance"><ComplianceTab /></TabsContent>
          <TabsContent value="pages"><CustomPagesTab /></TabsContent>
          <TabsContent value="dpo"><DPOContactsTab /></TabsContent>
          <TabsContent value="jobs"><JobApplicationsTab /></TabsContent>
          <TabsContent value="menu"><MenuSettingsTab /></TabsContent>
          <TabsContent value="footer"><FooterSettingsTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="security"><SecurityTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
